import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { useId, useState } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import '../../../shared/platform.css'
import type { SourceProductCard, SourceProductDescriptor, SourceProductRegistry } from '../model/descriptor'
import './sourceProduct.css'

export const SOURCE_UNSUPPORTED_TEXT = 'Данные источника здесь показать нельзя.'

export interface SourceProductExtensionProps {
  registry: SourceProductRegistry
  /** Вид ресурса связи. */
  kind: string
  resourceId: string
  remoteKey: string
  /** Открыть окно карточки источника (окно выбирает описание вида). */
  onOpenCard: (card: SourceProductCard) => void
}

/**
 * Расширение карточки одного источника в блоке «Источники» общей карточки (ADR-21): сводка, «Все поля источника» и кнопка
 * открытия карточки источника. Вид без описания — честная строка, без ошибки. Что за поля и как они называются, знает описание
 * вида; здесь вида нет.
 */
export function SourceProductExtension({ registry, kind, resourceId, remoteKey, onOpenCard }: SourceProductExtensionProps) {
  const descriptor = registry.descriptorOf(kind)
  if (descriptor === undefined) return <p className="platform-product__muted">{SOURCE_UNSUPPORTED_TEXT}</p>
  return <SourceProductExtensionBody descriptor={descriptor} resourceId={resourceId} remoteKey={remoteKey} onOpenCard={onOpenCard} />
}

function SourceProductExtensionBody({
  descriptor,
  resourceId,
  remoteKey,
  onOpenCard,
}: {
  descriptor: SourceProductDescriptor
  resourceId: string
  remoteKey: string
  onOpenCard: (card: SourceProductCard) => void
}) {
  const state = descriptor.useExtension(resourceId, remoteKey)
  const [expanded, setExpanded] = useState(false)
  const fieldsId = useId()
  const card = descriptor.openCard?.(resourceId, remoteKey)
  const view = state.view

  return (
    <div className="platform-source-extension">
      {state.error !== null && (
        <StatusMessage
          kind={state.error.kind === 'unauthorized' ? 'unauthorized' : state.error.kind === 'forbidden' ? 'forbidden' : 'error'}
          title="Не удалось прочитать данные источника"
          text={state.error.message}
          meta={`Код: ${state.error.code}${state.error.correlationId === null ? '' : ` · запрос ${state.error.correlationId}`}`}
          action={
            <Button variant="secondary" onClick={state.reload} actionId="common.retry">
              Повторить
            </Button>
          }
        />
      )}
      {state.error === null && view === null && <StatusMessage kind="loading" title="Читаем данные источника…" />}
      {view?.state === 'ok' && (
        <>
          <p className="platform-source-extension__summary">
            {view.fields.length === 0 ? 'Источник не хранит дополнительных данных этой карточки.' : view.summary}
          </p>
          {view.fields.length > 0 && (
            <>
              <Button
                variant="ghost"
                icon={expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                aria-expanded={expanded}
                aria-controls={fieldsId}
                onClick={() => setExpanded((open) => !open)}
                actionId="platform.common-product.source-fields-toggle"
              >
                {expanded ? 'Скрыть поля источника' : 'Все поля источника'}
              </Button>
              {expanded && (
                <dl id={fieldsId} className="platform-source-extension__fields" aria-label="Все поля источника">
                  {view.fields.map((field) => (
                    <div key={field.id}>
                      <dt>{field.label}</dt>
                      <dd>{field.valueText}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </>
          )}
        </>
      )}
      {view?.state === 'newer-schema' && (
        <p className="platform-product__muted" role="status">
          {SOURCE_UNSUPPORTED_TEXT} Источник отдал схему данных {view.got}, клиент читает {view.supported.join(', ')}; сами данные
          сохранены.
        </p>
      )}
      {view?.state === 'invalid' && (
        <p className="platform-product__muted" role="status">
          {SOURCE_UNSUPPORTED_TEXT} Данные не сошлись с описанием: {view.reason}.
        </p>
      )}
      {card !== undefined && (
        <Button
          icon={<ExternalLink size={16} aria-hidden="true" />}
          onClick={() => onOpenCard(card)}
          actionId="platform.common-product.source-open-card"
        >
          Открыть карточку источника
        </Button>
      )}
    </div>
  )
}
